import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  getTierLimits, 
  hasFeature as checkTierFeature, 
  hasTierAccess,
  SubscriptionTier, 
  TierLimits 
} from '@/lib/subscriptionFeatures';

interface CompanySubscription {
  companyId: string;
  tier: SubscriptionTier;
  limits: TierLimits;
  // AI Pool
  aiPoolLimit: number;
  aiPoolUsed: number;
  topupConversations: number;
  totalAiRemaining: number;
  // Sites
  maxSites: number;
  currentSites: number;
  canAddSite: boolean;
  // Workers
  workerCount: number;
  // Top-ups
  topupCountThisMonth: number;
  maxTopupsThisMonth: number;
}

interface UseSubscriptionFeaturesReturn {
  isLoading: boolean;
  subscription: CompanySubscription | null;
  hasFeature: (feature: keyof TierLimits['features']) => boolean;
  hasTierAccess: (requiredTier: SubscriptionTier) => boolean;
  canAddWorker: () => boolean;
  canUseAI: () => boolean;
  canTopup: () => boolean;
  getAIUsagePercentage: () => number;
  getSiteUsagePercentage: () => number;
  refresh: () => Promise<void>;
}

export function useSubscriptionFeatures(companyId?: string): UseSubscriptionFeaturesReturn {
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState<CompanySubscription | null>(null);

  const fetchSubscription = useCallback(async () => {
    if (!companyId) {
      setIsLoading(false);
      return;
    }

    try {
      // Use the database function to get subscription info
      const { data, error } = await supabase.rpc('get_company_subscription', {
        p_company_id: companyId
      });

      if (error) throw error;

      // Cast data to expected shape
      const subData = data as Record<string, any> | null;
      
      if (subData && !subData.error) {
        const tier = (subData.tier || 'free') as SubscriptionTier;
        
        setSubscription({
          companyId,
          tier,
          limits: getTierLimits(tier),
          aiPoolLimit: subData.ai_pool_limit || 100,
          aiPoolUsed: subData.ai_pool_used || 0,
          topupConversations: subData.topup_conversations || 0,
          totalAiRemaining: subData.total_ai_remaining ?? 0,
          maxSites: subData.max_sites || 1,
          currentSites: subData.current_sites || 0,
          canAddSite: subData.can_add_site ?? false,
          workerCount: subData.worker_count || 0,
          topupCountThisMonth: subData.topup_count_this_month || 0,
          maxTopupsThisMonth: subData.max_topups_this_month || 3,
        });
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription(null);
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Subscribe to realtime changes on companies table for this company
  useEffect(() => {
    if (!companyId) return;

    const channel = supabase
      .channel(`company-tier-${companyId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'companies',
          filter: `id=eq.${companyId}`
        },
        () => {
          // Re-fetch subscription when company data changes
          fetchSubscription();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId, fetchSubscription]);

  const checkFeature = (feature: keyof TierLimits['features']): boolean => {
    if (!subscription) return false; // Default to not allowed if no subscription data
    return checkTierFeature(subscription.tier, feature);
  };

  const checkCanAddWorker = (): boolean => {
    // All tiers have unlimited workers now
    return true;
  };

  const checkCanUseAI = (): boolean => {
    if (!subscription) return false;
    if (subscription.aiPoolLimit === -1) return true; // Unlimited
    return subscription.totalAiRemaining > 0;
  };

  const checkCanTopup = (): boolean => {
    if (!subscription) return false;
    if (subscription.maxTopupsThisMonth === -1) return true; // Unlimited for paid tiers
    return subscription.topupCountThisMonth < subscription.maxTopupsThisMonth;
  };

  const getAIUsagePercentage = (): number => {
    if (!subscription) return 0;
    if (subscription.aiPoolLimit === -1) return 0; // Unlimited
    const totalLimit = subscription.aiPoolLimit + subscription.topupConversations;
    if (totalLimit === 0) return 100;
    return ((subscription.aiPoolUsed) / subscription.aiPoolLimit) * 100;
  };

  const getSiteUsagePercentage = (): number => {
    if (!subscription) return 0;
    if (subscription.maxSites === -1) return 0; // Unlimited
    return (subscription.currentSites / subscription.maxSites) * 100;
  };

  // Check if current tier meets required tier
  const checkTierAccess = useCallback((requiredTier: SubscriptionTier): boolean => {
    return hasTierAccess(subscription?.tier, requiredTier);
  }, [subscription?.tier]);

  return {
    isLoading,
    subscription,
    hasFeature: checkFeature,
    hasTierAccess: checkTierAccess,
    canAddWorker: checkCanAddWorker,
    canUseAI: checkCanUseAI,
    canTopup: checkCanTopup,
    getAIUsagePercentage,
    getSiteUsagePercentage,
    refresh: fetchSubscription,
  };
}

// Hook to get user's company ID
export function useUserCompanyId() {
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserCompany = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setIsLoading(false);
          return;
        }

        const { data: profile } = await supabase
          .from('worker_profiles')
          .select('company_id, site_id')
          .eq('user_id', user.id)
          .single();

        setCompanyId(profile?.company_id || null);
        setSiteId(profile?.site_id || null);
      } catch (error) {
        console.error('Error fetching user company:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserCompany();
  }, []);

  return { companyId, siteId, isLoading };
}

// Legacy hook for backward compatibility
export function useUserSiteId() {
  const { siteId, isLoading } = useUserCompanyId();
  return { siteId, isLoading };
}
